import type { AuthTokens } from '@robu/shared';

import { newIdempotencyKey } from './idempotency';
import {
  clearTokens,
  emitSessionEnded,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from './token-storage';

/** Trailing slashes are stripped so path joining stays predictable. */
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/+$/, '');

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

/** Error shape produced by the API (NestJS exception filter). */
interface ApiErrorBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
  code?: string;
}

const FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Некорректный запрос.',
  401: 'Требуется вход в систему.',
  403: 'Недостаточно прав для этого действия.',
  404: 'Данные не найдены.',
  409: 'Действие конфликтует с текущим состоянием данных.',
  422: 'Данные не прошли проверку.',
  429: 'Слишком много запросов. Попробуйте позже.',
  500: 'Внутренняя ошибка сервера.',
  502: 'Сервер недоступен.',
  503: 'Сервис временно недоступен.',
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /** Every validation message the server returned, in order. */
  readonly messages: string[];
  readonly body: unknown;

  constructor(status: number, messages: string[], code: string | null, body: unknown) {
    const primary = messages[0] ?? FALLBACK_MESSAGES[status] ?? 'Неизвестная ошибка.';
    super(primary);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.messages = messages;
    this.body = body;
  }
}

/** Raised when the request never reached the server (offline, DNS, CORS). */
export class NetworkError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super('Не удалось связаться с сервером. Проверьте соединение.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Неизвестная ошибка.';
}

export function buildUrl(path: string, query?: QueryParams): string {
  const normalised = path.startsWith('/') ? path : `/${path}`;
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query ?? {})) {
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item === null || item === undefined || item === '') continue;
      search.append(key, String(item));
    }
  }

  const queryString = search.toString();
  return `${API_BASE_URL}${normalised}${queryString ? `?${queryString}` : ''}`;
}

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  query?: QueryParams;
  /**
   * TZ §35 — send an `Idempotency-Key`. Pass `true` to generate one, or a
   * stable string when the caller wants retries of the *same* user intent to
   * collapse into a single financial effect.
   */
  idempotent?: boolean | string;
  signal?: AbortSignal;
  /** Skip the Authorization header (login, refresh, password reset). */
  anonymous?: boolean;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    const text = await response.text();
    return text === '' ? undefined : text;
  }

  try {
    return (await response.json()) as unknown;
  } catch {
    return undefined;
  }
}

function toApiError(status: number, body: unknown): ApiError {
  if (typeof body === 'string' && body.trim() !== '') {
    return new ApiError(status, [body], null, body);
  }

  if (body && typeof body === 'object') {
    const parsed = body as ApiErrorBody;
    const raw = parsed.message;
    const messages = Array.isArray(raw) ? raw.filter((item) => typeof item === 'string') : [];
    if (typeof raw === 'string' && raw.trim() !== '') messages.push(raw);
    return new ApiError(status, messages, parsed.code ?? parsed.error ?? null, body);
  }

  return new ApiError(status, [], null, body);
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers = new Headers({ Accept: 'application/json' });

  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  if (token && !options.anonymous) headers.set('Authorization', `Bearer ${token}`);
  if (options.idempotent) {
    headers.set(
      'Idempotency-Key',
      typeof options.idempotent === 'string' ? options.idempotent : newIdempotencyKey(),
    );
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  try {
    return await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }
}

/**
 * Single-flight refresh.
 *
 * A dashboard renders half a dozen queries at once; when the access token
 * expires they all get a 401 simultaneously. Without this mutex each of them
 * would spend the (single-use, rotating) refresh token and all but one would
 * fail, logging the user out mid-session.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const response = await send(
    '/auth/refresh',
    { method: 'POST', body: { refreshToken }, anonymous: true },
    null,
  );

  if (!response.ok) {
    clearTokens();
    emitSessionEnded();
    return null;
  }

  const tokens = (await parseBody(response)) as AuthTokens | undefined;
  if (!tokens?.accessToken) {
    clearTokens();
    emitSessionEnded();
    return null;
  }

  setAccessToken(tokens.accessToken);
  // The API rotates refresh tokens; persist whatever it handed back.
  if (tokens.refreshToken) setRefreshToken(tokens.refreshToken);
  return tokens.accessToken;
}

export function refreshSession(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh()
      .catch(() => {
        clearTokens();
        emitSessionEnded();
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export async function apiRequest<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  let response = await send(path, options, getAccessToken());

  if (response.status === 401 && !options.anonymous) {
    const token = await refreshSession();
    if (token) {
      // The retry reuses the caller's Idempotency-Key only when it was stable;
      // a generated key would otherwise differ and defeat the guarantee.
      response = await send(path, options, token);
    }
  }

  const body = await parseBody(response);
  if (!response.ok) throw toApiError(response.status, body);
  return body as TResponse;
}

export const api = {
  get: <T>(path: string, query?: QueryParams, signal?: AbortSignal): Promise<T> =>
    apiRequest<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),

  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    apiRequest<T>(path, { ...options, method: 'POST', ...(body === undefined ? {} : { body }) }),

  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    apiRequest<T>(path, { ...options, method: 'PUT', ...(body === undefined ? {} : { body }) }),

  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', ...(body === undefined ? {} : { body }) }),

  delete: <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};
